(() => {
  'use strict';
  if (window.ppModelMenusReady) return;
  window.ppModelMenusReady = true;
  const finePointer = matchMedia('(min-width: 990px) and (hover: hover) and (pointer: fine)');
  const desktop = matchMedia('(min-width: 990px)');
  const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
  const controllers = new Map();
  const directories = new Map();

  function loadDirectory(endpoint) {
    if (directories.has(endpoint)) return directories.get(endpoint);
    const task = (async () => {
      let url = new URL(endpoint, location.origin);
      const seen = new Set();
      const cards = new Map();
      while (url) {
        if (url.origin !== location.origin || seen.has(url.href)) throw new Error('Invalid model directory page');
        seen.add(url.href);
        const abort = new AbortController();
        const timeout = setTimeout(() => abort.abort(), 12000);
        let html;
        try {
          const response = await fetch(url.href, { credentials: 'same-origin', signal: abort.signal });
          if (!response.ok) throw new Error('Model directory unavailable');
          html = await response.text();
        } finally { clearTimeout(timeout); }
        const page = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-ppm-directory]');
        if (!page) throw new Error('Missing model directory');
        page.querySelectorAll('.ppm-card[data-ppm-handle]').forEach(card => cards.set(card.dataset.ppmHandle, card));
        const next = page.dataset.nextPage;
        if (next) {
          url = new URL(next, url);
          url.searchParams.set('section_id', 'pp-model-directory');
        } else url = null;
      }
      return [...cards.values()];
    })();
    directories.set(endpoint, task);
    task.catch(() => directories.delete(endpoint));
    return task;
  }

  function init(root = document) {
    root.querySelectorAll('[data-ppm-menu]').forEach(menu => {
      if (controllers.has(menu)) return;
      const trigger = menu.querySelector('.ppm-trigger');
      const grid = menu.querySelector('[data-ppm-grid]');
      const picker = menu.querySelector('dialog.ppm-picker');
      const clearSearch = menu.querySelector('[data-ppm-clear]');
      const outerMenu = menu.closest('.pp-mobile-menu');
      const filter = menu.querySelector('[data-ppm-filter]');
      const count = menu.querySelector('[data-ppm-count]');
      const empty = menu.querySelector('[data-ppm-no-results]');
      const status = menu.querySelector('.ppm-load-status');
      const message = menu.querySelector('[data-ppm-load-message]');
      const retry = menu.querySelector('[data-ppm-retry]');
      let loaded = false, loading = false;
      filter.closest('.ppm-filter').hidden = false;
      function normalized(value) {
        const text = window.PartpointSearch?.normalize(value, false, document.documentElement.lang) || value;
        return text.normalize('NFKC').toLowerCase().replace(/[^\p{L}\p{N}]+/gu, ' ').trim();
      }
      function filterCards(resetScroll = false) {
        const words = normalized(filter.value).split(/\s+/).filter(Boolean);
        const cards = [...grid.querySelectorAll('.ppm-card')];
        let visible = 0;
        cards.forEach(card => {
          const title = normalized(card.getAttribute('aria-label') || card.textContent);
          card.hidden = !words.every(word => title.includes(word));
          if (!card.hidden) visible++;
        });
        count.textContent = words.length ? count.dataset.ppmResultLabel.replace('__COUNT__', visible) : `${cards.length} ${count.dataset.ppmTotalLabel}`;
        empty.hidden = visible > 0 || loading;
        if (clearSearch) clearSearch.hidden = !filter.value;
        if (resetScroll) { grid.scrollLeft = 0; grid.scrollTop = 0; }
        requestAnimationFrame(updateControls);
      }
      /* 2026-09-23 (PERF-1): მობილური grid სერვერიდან ცარიელი მოდის — ბარათებს დესკტოპის grid-იდან ვაკლონებთ
         (იგივე სია, იგივე რიგი). იდემპოტენტურია: თუ grid-ში უკვე არის ბარათი, არაფერს აკეთებს. */
      function seedFromTwin() {
        const sourceId = grid.dataset.ppmCloneFrom;
        if (!sourceId || grid.querySelector('.ppm-card')) return;
        const source = document.getElementById(sourceId);
        if (!source) return;
        const seed = document.createDocumentFragment();
        source.querySelectorAll(':scope > .ppm-card').forEach(card => {
          const copy = card.cloneNode(true);
          copy.hidden = false;
          seed.append(copy);
        });
        grid.append(seed);
      }
      async function refreshModels() {
        seedFromTwin();
        if (loaded || loading) return;
        loading = true;
        retry.hidden = true;
        message.textContent = message.dataset.loading;
        status.hidden = false;
        empty.hidden = true;
        try {
          const models = await loadDirectory(menu.dataset.ppmDirectoryUrl);
          const existing = new Set([...grid.querySelectorAll('.ppm-card')].map(card => card.dataset.ppmHandle));
          const additions = document.createDocumentFragment();
          models.forEach(source => {
            if (source.dataset.ppmBrand !== menu.dataset.ppmBrand || existing.has(source.dataset.ppmHandle)) return;
            const card = source.cloneNode(true);
            if (new URL(card.href, location.origin).pathname === location.pathname) card.setAttribute('aria-current', 'page');
            else card.removeAttribute('aria-current');
            additions.append(card);
            existing.add(source.dataset.ppmHandle);
          });
          grid.querySelectorAll(':scope > p').forEach(node => node.remove());
          grid.append(additions);
          loaded = true;
          status.hidden = true;
        } catch {
          message.textContent = message.dataset.error;
          retry.hidden = false;
        } finally {
          loading = false;
          filterCards();
        }
      }
      filter.addEventListener('input', () => filterCards(true));
      filter.addEventListener('search', () => filterCards(true));
      retry.addEventListener('click', refreshModels);
      clearSearch?.addEventListener('click', () => { filter.value = ''; filterCards(true); filter.focus({ preventScroll: true }); });
      filter.addEventListener('keydown', event => { if (picker && event.key === 'Enter') { event.preventDefault(); filter.blur(); } });
      picker?.querySelector('[data-ppm-back]').addEventListener('click', () => close(true));
      picker?.querySelector('[data-ppm-close]').addEventListener('click', () => close(true, true));
      picker?.addEventListener('cancel', event => { event.preventDefault(); close(true); });
      outerMenu?.addEventListener('toggle', () => { if (!outerMenu.open) close(); });
      let enterTimer, leaveTimer, closeTimer;
      function clearTimers() {
        clearTimeout(enterTimer); clearTimeout(leaveTimer); clearTimeout(closeTimer);
      }
      function fit() {
        const viewport = window.visualViewport;
        if (picker) {
          picker.style.setProperty('--ppm-picker-height', `${viewport?.height || innerHeight}px`);
          picker.style.setProperty('--ppm-picker-top', `${viewport?.offsetTop || 0}px`);
          return;
        }
        const header = menu.closest('.pp-header');
        if (!header) return;
        const bottom = viewport ? viewport.offsetTop + viewport.height : innerHeight;
        const headerBounds = header.getBoundingClientRect();
        const top = Math.max(viewport?.offsetTop || 0, headerBounds.top);
        const minimumHeight = Math.min(300, Math.max(0, bottom - top - 24));
        const offset = Math.min(0, bottom - headerBounds.bottom - 12 - minimumHeight);
        menu.style.setProperty('--ppm-panel-offset', `${offset}px`);
        menu.style.setProperty('--ppm-available-height', `${Math.max(0, bottom - headerBounds.bottom - offset - 12)}px`);
      }
      function updateControls() {
        const max = grid.scrollWidth - grid.clientWidth;
        menu.querySelectorAll('[data-ppm-scroll]').forEach(button => {
          button.disabled = Number(button.dataset.ppmScroll) < 0 ? grid.scrollLeft <= 2 : grid.scrollLeft >= max - 2;
        });
      }
      function close(animate = false, exitMenu = false) {
        clearTimers();
        if (!menu.open) return;
        trigger.setAttribute('aria-expanded', 'false');
        const finish = () => {
          if (picker?.open) picker.close();
          menu.open = false;
          menu.classList.remove('ppm-closing');
          if (picker) {
            document.documentElement.classList.remove('ppm-picker-open');
            if (exitMenu && outerMenu) { outerMenu.open = false; outerMenu.querySelector('summary').focus({ preventScroll: true }); }
            else if (outerMenu?.open && !desktop.matches) trigger.focus({ preventScroll: true });
          }
        };
        if (animate && !reducedMotion.matches) {
          menu.classList.add('ppm-closing');
          closeTimer = setTimeout(finish, picker ? 180 : 160);
        } else finish();
      }
      function open() {
        clearTimers();
        seedFromTwin();
        controllers.forEach((control, other) => { if (other !== menu) control.close(); });
        menu.classList.remove('ppm-closing');
        menu.open = true;
        trigger.setAttribute('aria-expanded', 'true');
        if (picker && !picker.open) {
          document.documentElement.classList.add('ppm-picker-open');
          picker.showModal();
          picker.querySelector('[data-ppm-back]').focus({ preventScroll: true });
        }
        fit();
        requestAnimationFrame(updateControls);
        refreshModels();
      }
      controllers.set(menu, { close, fit, updateControls });
      trigger.addEventListener('click', event => {
        event.preventDefault();
        if (menu.open && !menu.classList.contains('ppm-closing')) close(); else open();
      });
      menu.addEventListener('toggle', () => {
        trigger.setAttribute('aria-expanded', String(menu.open));
        if (menu.open) { fit(); updateControls(); }
        else if (picker?.open) { picker.close(); document.documentElement.classList.remove('ppm-picker-open'); }
      });
      menu.addEventListener('pointerenter', () => {
        if (!finePointer.matches) return;
        clearTimers();
        menu.classList.remove('ppm-closing');
        if (!menu.open) enterTimer = setTimeout(open, 100);
        else trigger.setAttribute('aria-expanded', 'true');
      });
      menu.addEventListener('pointerleave', () => {
        if (!finePointer.matches) return;
        clearTimeout(enterTimer);
        if (!menu.contains(document.activeElement)) leaveTimer = setTimeout(() => close(true), 220);
      });
      menu.addEventListener('keydown', event => {
        if (event.key === 'Escape' && menu.open) {
          event.preventDefault(); event.stopPropagation(); close(Boolean(picker)); if (!picker) trigger.focus();
        } else if (event.key === 'ArrowDown' && event.target === trigger) {
          event.preventDefault(); open(); grid.querySelector('.ppm-card:not([hidden])')?.focus();
        }
      });
      menu.addEventListener('focusout', () => {
        setTimeout(() => {
          if (!picker?.open && !menu.contains(document.activeElement) && (!finePointer.matches || !menu.matches(':hover'))) close();
        }, 0);
      });
      menu.querySelectorAll('[data-ppm-scroll]').forEach(button => {
        button.addEventListener('click', () => {
          const card = grid.querySelector('.ppm-card:not([hidden])');
          const step = card ? card.getBoundingClientRect().width + 10 : grid.clientWidth * .85;
          grid.scrollBy({ left: Number(button.dataset.ppmScroll) * step, behavior: reducedMotion.matches ? 'instant' : 'smooth' });
        });
      });
      grid.addEventListener('scroll', updateControls, { passive: true });
    });
  }
  document.addEventListener('pointerdown', event => {
    controllers.forEach((control, menu) => { if (menu.open && !menu.contains(event.target)) control.close(); });
  });
  desktop.addEventListener('change', () => controllers.forEach(control => control.close()));
  window.addEventListener('resize', () => controllers.forEach((control, menu) => {
    if (menu.open) { control.fit(); control.updateControls(); }
  }), { passive: true });
  window.visualViewport?.addEventListener('resize', () => controllers.forEach((control, menu) => {
    if (menu.open) control.fit();
  }), { passive: true });
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => {
    controllers.forEach((control, menu) => {
      if (event.target.contains(menu)) { control.close(); controllers.delete(menu); }
    });
  });
  init();
})();
